import React, { useState, useEffect } from 'react';
import { 
  View, 
  Text, 
  TextInput, 
  TouchableOpacity, 
  StyleSheet, 
  ScrollView,
  ActivityIndicator,
  Modal,
  useWindowDimensions
} from 'react-native';
import { useTheme } from '../contexts/ThemeContext';
import { getTemplates, getProjectExamples, getUserPreferences, trackTemplateUsage } from '../services/firestoreService';
import { generateProposal, recommendExamples, regenerateProposal, answerProposalQuestions, generateContraVersion } from '../services/openaiService';
import { analyzeProposal, highlightChanges } from '../services/aiAssistantService';
import { getUserProfile, getUserScopeFilter } from '../services/profileService';
import { logGenerationMetric, getUserAverageGenerationTime, formatTime } from '../services/metricsService';
import { auth } from '../firebaseConfig';
import AIAssistantPanel from '../components/AIAssistantPanel';
import ExamplesSidebar from '../components/ExamplesSidebar';
import ProposalDisplay from '../components/ProposalDisplay';
import UserMenu from '../components/UserMenu';
import LandingStyleHeader from '../components/LandingStyleHeader';
import Toast from '../components/Toast';

export default function ProposalWizardScreen({ navigation }) {
  const { colors, glass, isDark, gradients } = useTheme();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const isTablet = width >= 768 && width < 1024;
  const isDesktop = width >= 1024;

  const [step, setStep] = useState(1); // 1: description, 2: template, 3: examples, 4: result
  const [projectDescription, setProjectDescription] = useState('');
  const [clientName, setClientName] = useState('');
  const [templates, setTemplates] = useState([]);
  const [allExamples, setAllExamples] = useState([]);
  const [scopeFilter, setScopeFilter] = useState('all'); // all, organization, personal
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [recommendedExamples, setRecommendedExamples] = useState([]);
  const [selectedExamples, setSelectedExamples] = useState([]);
  const [generatedProposal, setGeneratedProposal] = useState('');
  const [editableProposal, setEditableProposal] = useState('');
  const [loading, setLoading] = useState(false);
  const [customQuestions, setCustomQuestions] = useState([]);
  const [questionAnswers, setQuestionAnswers] = useState({});
  const [showExampleSelector, setShowExampleSelector] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [aiRecommendations, setAiRecommendations] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [ignoredTasks, setIgnoredTasks] = useState([]);
  const [showAssistant, setShowAssistant] = useState(false);
  const [highlightedSegments, setHighlightedSegments] = useState([]);
  const [updatingProposal, setUpdatingProposal] = useState(false);
  const [showMobileExamples, setShowMobileExamples] = useState(false);
  const [showMobileAssistant, setShowMobileAssistant] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [customQuestionInput, setCustomQuestionInput] = useState('');
  const [characterMaximum, setCharacterMaximum] = useState(null); // null means no limit
  const [generationStartTime, setGenerationStartTime] = useState(null);
  const [generationStatus, setGenerationStatus] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [userAverageTime, setUserAverageTime] = useState(15000); // Default 15 seconds
  const [strictAdherenceDefault, setStrictAdherenceDefault] = useState(false);
  const [showExamplesSidebar, setShowExamplesSidebar] = useState(true);
  const [showAISidebar, setShowAISidebar] = useState(true);
  const [showProjectDescription, setShowProjectDescription] = useState(false); // Collapsed by default
  const [previewProposal, setPreviewProposal] = useState('');
  const [showQuestions, setShowQuestions] = useState(false);
  const [contraLoading, setContraLoading] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ visible: true, message, type });
  };

  useEffect(() => {
    loadData();
    loadUserProfile();
    
    // Check for pre-filled content from dashboard
    const pendingContent = sessionStorage.getItem('pendingProjectDescription');
    if (pendingContent) {
      setProjectDescription(pendingContent);
      sessionStorage.removeItem('pendingProjectDescription');
    }
    loadUserAverageTime();
  }, []);

  // Real-time elapsed time counter
  useEffect(() => {
    let interval;
    if (generationStartTime && loading) {
      interval = setInterval(() => {
        const elapsed = Date.now() - generationStartTime;
        setElapsedTime(elapsed);
      }, 100); // Update every 100ms for smooth animation
    } else {
      setElapsedTime(0);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [generationStartTime, loading]);

  const loadUserAverageTime = async () => {
    try {
      if (auth.currentUser) {
        const avgTime = await getUserAverageGenerationTime(auth.currentUser.uid);
        setUserAverageTime(avgTime);
      }
    } catch (error) {
      console.error('Error loading user average time:', error);
    }
  };

  useEffect(() => {
    // Update document title based on current step
    const titles = {
      1: 'Flomodo - Project Description',
      2: 'Flomodo - Select Template',
      3: 'Flomodo - Select Examples',
      4: 'Flomodo - Generated Proposal'
    };
    document.title = titles[step] || 'Flomodo';
  }, [step]);

  // Real-time preview update when examples change
  useEffect(() => {
    if (step === 4 && selectedExamples.length > 0 && selectedTemplate) {
      updatePreview();
    }
  }, [selectedExamples]);

  const updatePreview = () => {
    // Create a quick preview showing where examples will appear
    if (!selectedTemplate || !generatedProposal) return;
    
    // Show selected examples in the preview
    const examplesPreview = selectedExamples.map((ex, idx) => 
      `${idx + 1}. ${ex.title}${ex.technologies ? ` (${ex.technologies})` : ''}`
    ).join('\n');
    
    setPreviewProposal(`Selected Examples (${selectedExamples.length}):\n${examplesPreview}\n\n[These will be integrated into your proposal]`);
  };

  const loadUserProfile = async () => {
    try {
      const profile = await getUserProfile(auth.currentUser.uid);
      setUserProfile(profile);
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [templatesData, examplesData, preferencesData, userScopeFilter] = await Promise.all([
        getTemplates(),
        getProjectExamples(),
        getUserPreferences(),
        getUserScopeFilter(auth.currentUser.uid)
      ]);
      
      // Set scope filter
      setScopeFilter(userScopeFilter);
      
      // Filter to only show proposal templates (handle both 'type' and 'templateType' fields)
      let proposalTemplates = templatesData.filter(t => {
        // Check templateType first, then type, default to 'proposal' if missing/empty
        const type = (t.templateType || t.type || '').toLowerCase().trim() || 'proposal';
        return type === 'proposal';
      });
      
      // Apply scope filter
      if (userScopeFilter !== 'all') {
        proposalTemplates = proposalTemplates.filter(t => t.scope === userScopeFilter);
      }
      
      // Sort templates by usage count (most used first)
      const sortedTemplates = proposalTemplates.sort((a, b) => {
        const usageA = a.usageCount || 0;
        const usageB = b.usageCount || 0;
        return usageB - usageA; // Descending order
      });
      
      setTemplates(sortedTemplates);
      
      // Apply scope filter to examples
      let filteredExamples = examplesData;
      if (userScopeFilter !== 'all') {
        filteredExamples = examplesData.filter(e => e.scope === userScopeFilter);
      }
      
      console.log('📋 Templates & Examples loaded:', {
        totalTemplates: templatesData.length,
        proposalTemplates: proposalTemplates.length,
        totalExamples: examplesData.length,
        filteredExamples: filteredExamples.length,
        scopeFilter: userScopeFilter
      });
      setAllExamples(filteredExamples);
      setStrictAdherenceDefault(preferencesData.strictAdherenceDefault || false);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Error loading data. Please check your internet connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    if (step === 1) {
      if (!projectDescription.trim()) {
        alert('Please enter a project description');
        return;
      }
      if (templates.length === 0) {
        alert('No proposal templates found. Please create at least one proposal template in Settings → Templates & Examples first.');
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (!selectedTemplate) {
        alert('Please select a template');
        return;
      }
      // Auto-recommend examples
      await handleAutoRecommend();
      setStep(3);
    } else if (step === 3) {
      if (selectedExamples.length === 0) {
        alert('Please select at least one example');
        return;
      }
      await handleGenerateProposal();
    }
  };

  const handleAutoRecommend = async () => {
    if (allExamples.length === 0) {
      alert('Please add some project examples in Settings first');
      return;
    }

    setLoading(true);
    try {
      const recommended = await recommendExamples(projectDescription, allExamples);
      setRecommendedExamples(recommended);
      setSelectedExamples(recommended.slice(0, 4)); // Start with up to 4
    } catch (error) {
      alert('Error recommending examples: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateProposal = async () => {
    const startTime = Date.now();
    setGenerationStartTime(startTime);
    setLoading(true);
    
    try {
      // Track template usage
      if (selectedTemplate?.id) {
        trackTemplateUsage(selectedTemplate.id).catch(err => 
          console.log('Failed to track template usage:', err)
        );
      }
      
      setGenerationStatus('Analyzing your project description...');
      await new Promise(resolve => setTimeout(resolve, 300)); // Brief pause for UX
      
      setGenerationStatus('✨ Generating your proposal...');
      const proposal = await generateProposal(
        selectedTemplate.content,
        projectDescription,
        selectedExamples,
        selectedTemplate.strictAdherence || false,
        characterMaximum,
        clientName
      );
      setGeneratedProposal(proposal);
      setEditableProposal(proposal);
      
      // Generate answers for custom questions if any
      if (customQuestions.length > 0 && customQuestions.some(q => q.trim())) {
        setGenerationStatus('💬 Answering custom questions...');
        const validQuestions = customQuestions.filter(q => q.trim());
        try {
          const answers = await answerProposalQuestions(
            validQuestions,
            proposal,
            projectDescription,
            selectedExamples,
            userProfile // Pass user profile for GitHub/website links
          );
          setQuestionAnswers(answers);
        } catch (error) {
          console.error('Error generating question answers:', error);
          // Continue anyway, questions are optional
        }
      }
      
      // Calculate elapsed time
      const endTime = Date.now();
      const elapsedTime = endTime - startTime;
      
      setGenerationStatus('✅ Complete!');
      
      // Log metrics to Firestore
      logGenerationMetric({
        userId: auth.currentUser?.uid,
        userEmail: auth.currentUser?.email,
        elapsedTime,
        templateId: selectedTemplate?.id,
        templateName: selectedTemplate?.name,
        exampleCount: selectedExamples.length,
        projectDescriptionLength: projectDescription.length,
        proposalLength: proposal.length,
        hasCustomQuestions: customQuestions.length > 0,
        customQuestionCount: customQuestions.filter(q => q.trim()).length,
        status: 'success'
      });
      
      setStep(4);
      
      // Analyze proposal and get recommendations
      setGenerationStatus('🤖 Analyzing proposal quality...');
      handleAnalyzeProposal(proposal);
      
      setGenerationStatus('');
    } catch (error) {
      const endTime = Date.now();
      const elapsedTime = endTime - startTime;
      
      // Log failed generation
      logGenerationMetric({
        userId: auth.currentUser?.uid,
        userEmail: auth.currentUser?.email,
        elapsedTime,
        templateId: selectedTemplate?.id,
        exampleCount: selectedExamples.length,
        status: 'error',
        errorMessage: error.message
      });
      
      alert('Error generating proposal: ' + error.message);
      setGenerationStatus('');
    } finally {
      setLoading(false);
      setGenerationStartTime(null);
    }
  };

  const handleAnalyzeProposal = async (proposal) => {
    try {
      const recommendations = await analyzeProposal(
        selectedTemplate.content,
        proposal,
        projectDescription
      );
      setAiRecommendations(recommendations);
    } catch (error) {
      console.error('Error analyzing proposal:', error);
    }
  };

  const handleRealTimeExampleUpdate = async () => {
    setUpdatingProposal(true);
    try {
      const proposal = await generateProposal(
        selectedTemplate.content,
        projectDescription,
        selectedExamples,
        selectedTemplate.strictAdherence || false,
        characterMaximum,
        clientName
      );
      setGeneratedProposal(proposal);
      setEditableProposal(proposal);
    } catch (error) {
      console.error('Error updating proposal:', error);
    } finally {
      setUpdatingProposal(false);
    }
  };

  const toggleTask = (taskIndex, action) => {
    if (action === 'complete') {
      setCompletedTasks([...completedTasks, taskIndex]);
      setIgnoredTasks(ignoredTasks.filter(i => i !== taskIndex));
    } else if (action === 'ignore') {
      setIgnoredTasks([...ignoredTasks, taskIndex]);
      setCompletedTasks(completedTasks.filter(i => i !== taskIndex));
    } else if (action === 'undo') {
      setCompletedTasks(completedTasks.filter(i => i !== taskIndex));
      setIgnoredTasks(ignoredTasks.filter(i => i !== taskIndex));
    }
  };

  const handleRegenerateProposal = async () => {
    console.log(`Regenerating with ${selectedExamples.length} examples:`, selectedExamples.map(e => e.title));
    setLoading(true);
    try {
      const proposal = await regenerateProposal(
        selectedTemplate.content,
        projectDescription,
        selectedExamples,
        generatedProposal,
        selectedTemplate.strictAdherence || false
      );
      setGeneratedProposal(proposal);
      setEditableProposal(proposal);
    } catch (error) {
      alert('Error regenerating proposal: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProposalWithExamples = async () => {
    // Regenerate proposal with new example selection
    setLoading(true);
    try {
      const proposal = await generateProposal(
        selectedTemplate.content,
        projectDescription,
        selectedExamples,
        selectedTemplate.strictAdherence || false,
        characterMaximum,
        clientName
      );
      setGeneratedProposal(proposal);
      setEditableProposal(proposal);
    } catch (error) {
      alert('Error updating proposal: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleExampleSelection = (example) => {
    const isSelected = selectedExamples.find(e => e.id === example.id);
    
    if (isSelected) {
      setSelectedExamples(selectedExamples.filter(e => e.id !== example.id));
    } else {
      if (selectedExamples.length >= 6) {
        alert('Maximum 6 examples allowed');
        return;
      }
      setSelectedExamples([...selectedExamples, example]);
    }
  };

  const moveExample = (index, direction) => {
    const newExamples = [...selectedExamples];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newExamples.length) return;
    
    [newExamples[index], newExamples[targetIndex]] = [newExamples[targetIndex], newExamples[index]];
    setSelectedExamples(newExamples);
  };

  const copyToClipboard = (customText = null, customMessage = null) => {
    // Ignore if first param is an event object (from TouchableOpacity)
    const isEvent = customText && typeof customText === 'object' && customText.nativeEvent;
    
    const textToCopy = (isEvent || !customText) 
      ? (isEditing ? editableProposal : generatedProposal)
      : customText;
    const message = customMessage || 'Proposal copied to clipboard!';
    
    if (navigator.clipboard) {
      navigator.clipboard.writeText(textToCopy);
      showToast(message, 'success');
    }
  };

  const handleCopyForContra = async () => {
    setContraLoading(true);
    try {
      const proposalText = isEditing ? editableProposal : generatedProposal;
      const contraVersion = await generateContraVersion(proposalText, userProfile, selectedExamples);
      
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(contraVersion);
        showToast(`Contra proposal copied! (${contraVersion.length} chars)`, 'success');
      }
    } catch (error) {
      console.error('Error generating Contra version:', error);
      showToast('Error generating Contra version', 'error');
    } finally {
      setContraLoading(false);
    }
  };

  const handleReset = () => {
    setStep(1);
    setProjectDescription('');
    setClientName('');
    setSelectedTemplate(null);
    setRecommendedExamples([]);
    setSelectedExamples([]);
    setGeneratedProposal('');
    setEditableProposal('');
    setIsEditing(false);
    setCustomQuestions([]);
    setQuestionAnswers({});
    setShowProjectDescription(false);
    setShowQuestions(false);
    setPreviewProposal('');
  };

  const handleStartOver = () => {
    setStep(1);
    // Keep all fields intact, just go back to step 1
  };

  if (loading && step < 4) {
    const progress = Math.min((elapsedTime / userAverageTime) * 100, 100);
    
    return (
      <View style={[styles.loadingContainer, { backgroundImage: isDark ? gradients.background : 'none', backgroundColor: colors.primary }]}>
        <ActivityIndicator size="large" color={colors.accent} />
        <Text style={[styles.loadingText, { color: colors.accent }]}>
          {generationStatus || (step === 2 ? 'Analyzing project...' : step === 3 ? 'Generating proposal...' : 'Loading...')}
        </Text>
        {generationStatus && (
          <View style={[styles.statusBar, { backgroundColor: colors.border }]}>
            <View style={[styles.statusBarFill, { width: `${progress}%`, backgroundColor: colors.accent }]} />
          </View>
        )}
        <Text style={[styles.estimateText, { color: colors.textSecondary }]}>
          Elapsed: {formatTime(elapsedTime)} / Avg: {formatTime(userAverageTime)}
        </Text>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundImage: gradients.background }]}>
      <LandingStyleHeader navigation={navigation} currentScreen="wizard" />

      <ScrollView 
        style={styles.content} 
        contentContainerStyle={step === 4 ? styles.contentContainerFull : styles.contentContainer}
        onScroll={(e) => {
          // Manually dispatch scroll event to window so header can detect it
          const scrollY = e.nativeEvent.contentOffset.y;
          window.lastKnownScrollY = scrollY;
          window.dispatchEvent(new Event('scroll'));
        }}
        scrollEventThrottle={16}
      >
        {/* STEP 1: Project Description */}
        {step === 1 && (
          <View style={[styles.stepContainer, styles.sectionSpacing]}>
            <Text style={[styles.stepTitle, { color: colors.text }]}>Step 1: Project Description</Text>
            <Text style={[styles.stepDescription, { color: colors.textSecondary }]}>
              Enter the job description or project requirements
            </Text>

            <Text style={[styles.inputLabel, { color: colors.text, marginTop: 0, marginBottom: 8 }]}>Client/Poster Name (Optional)</Text>
            <TextInput
              style={[styles.input, { backgroundColor: colors.secondary, borderColor: colors.border, color: colors.text, marginBottom: 20 }]}
              placeholder="e.g., John Smith or ABC Company"
              placeholderTextColor={colors.textTertiary}
              value={clientName}
              onChangeText={setClientName}
            />

            <Text style={[styles.inputLabel, { color: colors.text, marginTop: 0, marginBottom: 8 }]}>Project Description</Text>
            <TextInput
              style={[styles.textArea, { backgroundColor: colors.secondary, borderColor: colors.border, color: colors.text }]}
              placeholder="Paste the project description here..."
              placeholderTextColor={colors.textTertiary}
              value={projectDescription}
              onChangeText={setProjectDescription}
              multiline
              numberOfLines={15}
            />

            {/* Proposal Q&A Section - Collapsible */}
            <View style={[styles.questionsSection, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
              <TouchableOpacity 
                style={styles.questionsHeader}
                onPress={() => setShowQuestions(!showQuestions)}
              >
                <Text style={[styles.questionsTitle, { color: colors.accent }]}>
                  {showQuestions ? '▼' : '▶'} Proposal Q&A {customQuestions.length > 0 && `(${customQuestions.length})`}
                </Text>
                <Text style={[styles.questionsToggle, { color: colors.textSecondary }]}>
                  {showQuestions ? 'Hide' : 'Show'}
                </Text>
              </TouchableOpacity>
              
              {showQuestions && (
                <View style={styles.questionsContent}>
                  <Text style={[styles.questionsDesc, { color: colors.textSecondary }]}>
                    Add up to 10 client questions. AI will generate answers using your proposal and examples.
                  </Text>

                  {customQuestions.length > 0 && (
                    <View style={styles.questionsList}>
                      {customQuestions.map((question, index) => (
                        <View key={index} style={styles.questionRow}>
                          <View style={[styles.questionNumber, { backgroundColor: colors.accent }]}>
                            <Text style={[styles.questionNumberText, { color: '#fff' }]}>{index + 1}</Text>
                          </View>
                          <TextInput
                            style={[styles.questionInput, { backgroundColor: colors.secondary, borderColor: colors.border, color: colors.text }]}
                            placeholder="Paste client question here..."
                            placeholderTextColor={colors.textTertiary}
                            value={question}
                            onChangeText={(text) => {
                              const newQuestions = [...customQuestions];
                              newQuestions[index] = text;
                              setCustomQuestions(newQuestions);
                            }}
                            multiline
                          />
                          <TouchableOpacity
                            style={styles.removeQuestionButton}
                            onPress={() => {
                              setCustomQuestions(customQuestions.filter((_, i) => i !== index));
                            }}
                          >
                            <Text style={styles.removeQuestionText}>✕</Text>
                          </TouchableOpacity>
                        </View>
                      ))}
                    </View>
                  )}

                  {customQuestions.length < 10 && (
                    <View style={styles.addQuestionSection}>
                      <Text style={[styles.addQuestionLabel, { color: colors.text }]}>Add Custom Question {customQuestions.length > 0 && `(${customQuestions.length}/10)`}</Text>
                      <Text style={[styles.addQuestionSubtext, { color: colors.textSecondary }]}>
                        Type your own question or choose from common examples below
                      </Text>
                
                {/* Direct Custom Question Input */}
                <View style={styles.customQuestionInputRow}>
                  <TextInput
                    style={[styles.customQuestionInput, { backgroundColor: colors.secondary, borderColor: colors.border, color: colors.text }]}
                    placeholder="Write a custom question..."
                    placeholderTextColor={colors.textTertiary}
                    value={customQuestionInput}
                    onChangeText={setCustomQuestionInput}
                    onSubmitEditing={() => {
                      const text = customQuestionInput.trim();
                      if (text && customQuestions.length < 10) {
                        setCustomQuestions([...customQuestions, text]);
                        setCustomQuestionInput('');
                      }
                    }}
                    multiline
                  />
                  <TouchableOpacity
                    style={[styles.addQuestionButton, { backgroundColor: colors.accent }]}
                    onPress={() => {
                      const text = customQuestionInput.trim();
                      if (text && customQuestions.length < 10) {
                        setCustomQuestions([...customQuestions, text]);
                        setCustomQuestionInput('');
                      }
                    }}
                    disabled={!customQuestionInput.trim()}
                  >
                    <Text style={styles.addQuestionButtonText}>+ Add</Text>
                  </TouchableOpacity>
                </View>

                {/* Common Question Suggestions */}
                <View style={[styles.commonQuestionsContainer, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
                  <Text style={[styles.commonQuestionsTitle, { color: colors.text }]}>Common Questions (Tap to Add)</Text>
                  <View style={styles.commonQuestionsGrid}>
                    {[
                      'Describe your recent experience with similar projects',
                      'Please list any certifications related to this project',
                      'Include a link to your GitHub profile and/or website',
                      'What frameworks have you worked with?',
                      'Describe your approach to testing and improving QA'
                    ].map((question, idx) => (
                      <TouchableOpacity
                        key={idx}
                        style={[styles.commonQuestionChip, { backgroundColor: colors.secondary, borderColor: colors.border }]}
                        onPress={() => {
                          if (customQuestions.length < 10) {
                            setCustomQuestions([...customQuestions, question]);
                          } else {
                            alert('Maximum 10 questions allowed');
                          }
                        }}
                      >
                        <Text style={[styles.commonQuestionChipText, { color: colors.textSecondary }]}>+ {question}</Text>
                      </TouchableOpacity>
                    ))}
                    </View>
                  </View>
                </View>
                  )}
                </View>
              )}
            </View>

            <TouchableOpacity 
              style={styles.primaryButton} 
              onPress={handleNext}
            >
              <Text style={styles.primaryButtonText}>Next: Select Template →</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* STEP 2: Template Selection */}
        {step === 2 && (
          <View style={[styles.stepContainer, styles.sectionSpacing]}>
            <Text style={[styles.stepTitle, { color: colors.text }]}>Step 2: Select Template</Text>
            <Text style={[styles.stepDescription, { color: colors.textSecondary }]}>
              Choose a proposal template to use
            </Text>

            {/* Organization Templates */}
            {templates.some(t => t.scope === 'organization') && (
              <>
                <View style={[styles.templatesSectionHeader, { borderBottomColor: colors.border }]}>
                  <Text style={[styles.templatesSectionTitle, { color: colors.accent }]}>🏢 Organization Templates</Text>
                  <Text style={[styles.templatesSectionSubtitle, { color: colors.textSecondary }]}>
                    Shared with your team
                  </Text>
                </View>
                
                {templates.filter(t => t.scope === 'organization').map((template, index) => {
                  const isPopular = (template.usageCount || 0) > 5; // Show if used 5+ times
                  const usageCount = template.usageCount || 0;
                  
                  return (
                    <TouchableOpacity
                      key={template.id}
                      style={[
                        styles.selectionCard,
                        { ...glass, backgroundColor: colors.card, borderColor: colors.border },
                        selectedTemplate?.id === template.id && styles.selectedCard
                      ]}
                      onPress={() => setSelectedTemplate({
                        ...template,
                        strictAdherence: strictAdherenceDefault
                      })}
                    >
                      <View style={styles.radioContainer}>
                        <View style={[
                          styles.radio,
                          { borderColor: colors.border },
                          selectedTemplate?.id === template.id && [styles.radioSelected, { borderColor: colors.accent, backgroundColor: colors.accent }]
                        ]} />
                        <View style={{flex: 1}}>
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                            <Text style={[styles.selectionTitle, { color: colors.text, flex: 1 }]}>{template.name}</Text>
                            {isPopular && (
                              <View style={[styles.popularBadge, { backgroundColor: colors.accent }]}>
                                <Text style={styles.popularBadgeText}>⭐ {usageCount} uses</Text>
                              </View>
                            )}
                          </View>
                          <Text style={[styles.selectionPreview, { color: colors.textSecondary }]} numberOfLines={2}>
                            {template.content}
                          </Text>
                        </View>
                      </View>
                    </TouchableOpacity>
                  );
                })}
              </>
            )}

            {/* Personal Templates */}
            {templates.some(t => t.scope === 'personal') && (
              <>
                <View style={[styles.templatesSectionHeader, { borderBottomColor: colors.border, marginTop: templates.some(t => t.scope === 'organization') ? 30 : 0 }]}>
                  <Text style={[styles.templatesSectionTitle, { color: colors.accent }]}>👤 My Personal Templates</Text>
                  <Text style={[styles.templatesSectionSubtitle, { color: colors.textSecondary }]}>
                    Only visible to you
                  </Text>
                </View>
                
                {templates.filter(t => t.scope === 'personal').map((template, index) => {
                  const isPopular = (template.usageCount || 0) > 5;
                  const usageCount = template.usageCount || 0;
                  
                  return (
                    <TouchableOpacity
                      key={template.id}
                      style={[
                        styles.selectionCard,
                        { ...glass, backgroundColor: colors.card, borderColor: colors.border },
                        selectedTemplate?.id === template.id && styles.selectedCard
                      ]}
                      onPress={() => setSelectedTemplate({
                        ...template,
                        strictAdherence: strictAdherenceDefault
                      })}
                    >
                      <View style={styles.radioContainer}>
                        <View style={[
                          styles.radio,
                          { borderColor: colors.border },
                          selectedTemplate?.id === template.id && [styles.radioSelected, { borderColor: colors.accent, backgroundColor: colors.accent }]
                        ]} />
                        <View style={{flex: 1}}>
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                            <Text style={[styles.selectionTitle, { color: colors.text, flex: 1 }]}>{template.name}</Text>
                            {isPopular && (
                              <View style={[styles.popularBadge, { backgroundColor: colors.accent }]}>
                                <Text style={styles.popularBadgeText}>⭐ {usageCount} uses</Text>
                              </View>
                            )}
                          </View>
                          <Text style={[styles.selectionPreview, { color: colors.textSecondary }]} numberOfLines={2}>
                            {template.content}
                          </Text>
                        </View>
                      </View>
                    </TouchableOpacity>
                  );
                })}
              </>
            )}

            {selectedTemplate && (
              <View style={[styles.strictModeContainer, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
                <TouchableOpacity 
                  style={styles.checkboxRow}
                  onPress={() => setSelectedTemplate({
                    ...selectedTemplate,
                    strictAdherence: !selectedTemplate.strictAdherence
                  })}
                >
                  <View style={[
                    styles.checkbox,
                    { borderColor: colors.border },
                    selectedTemplate.strictAdherence && [styles.checkboxChecked, { borderColor: colors.text, backgroundColor: colors.text }]
                  ]}>
                    {selectedTemplate.strictAdherence && (
                      <Text style={[styles.checkmark, { color: '#fff' }]}>✓</Text>
                    )}
                  </View>
                  <View style={{flex: 1}}>
                    <Text style={[styles.checkboxLabel, { color: colors.text }]}>Strict Adherence Mode</Text>
                    <Text style={[styles.checkboxDesc, { color: colors.textSecondary }]}>
                      Only change content in [brackets]. Everything else stays exactly the same.
                    </Text>
                  </View>
                </TouchableOpacity>
                
                {/* Character Limit - Subtle inline control */}
                <View style={styles.characterLimitRow}>
                  <Text style={[styles.characterLimitLabel, { color: colors.text }]}>Character Limit:</Text>
                  <View style={styles.characterLimitButtons}>
                    <TouchableOpacity
                      style={[styles.charLimitBtn, { backgroundColor: colors.secondary, borderColor: colors.border }, characterMaximum === null && [styles.charLimitBtnActive, { backgroundColor: colors.accent, borderColor: colors.accent }]]}
                      onPress={() => setCharacterMaximum(null)}
                    >
                      <Text style={[styles.charLimitBtnText, { color: colors.textSecondary }, characterMaximum === null && [styles.charLimitBtnTextActive, { color: '#fff' }]]}>None</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.charLimitBtn, { backgroundColor: colors.secondary, borderColor: colors.border }, characterMaximum === 500 && [styles.charLimitBtnActive, { backgroundColor: colors.accent, borderColor: colors.accent }]]}
                      onPress={() => setCharacterMaximum(500)}
                    >
                      <Text style={[styles.charLimitBtnText, { color: colors.textSecondary }, characterMaximum === 500 && [styles.charLimitBtnTextActive, { color: '#fff' }]]}>500</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.charLimitBtn, { backgroundColor: colors.secondary, borderColor: colors.border }, characterMaximum === 1500 && [styles.charLimitBtnActive, { backgroundColor: colors.accent, borderColor: colors.accent }]]}
                      onPress={() => setCharacterMaximum(1500)}
                    >
                      <Text style={[styles.charLimitBtnText, { color: colors.textSecondary }, characterMaximum === 1500 && [styles.charLimitBtnTextActive, { color: '#fff' }]]}>1500</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.charLimitBtn, { backgroundColor: colors.secondary, borderColor: colors.border }, characterMaximum === 2500 && [styles.charLimitBtnActive, { backgroundColor: colors.accent, borderColor: colors.accent }]]}
                      onPress={() => setCharacterMaximum(2500)}
                    >
                      <Text style={[styles.charLimitBtnText, { color: colors.textSecondary }, characterMaximum === 2500 && [styles.charLimitBtnTextActive, { color: '#fff' }]]}>2500</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.charLimitBtn, { backgroundColor: colors.secondary, borderColor: colors.border }, characterMaximum === 5000 && [styles.charLimitBtnActive, { backgroundColor: colors.accent, borderColor: colors.accent }]]}
                      onPress={() => setCharacterMaximum(5000)}
                    >
                      <Text style={[styles.charLimitBtnText, { color: colors.textSecondary }, characterMaximum === 5000 && [styles.charLimitBtnTextActive, { color: '#fff' }]]}>5000</Text>
                    </TouchableOpacity>
                    <TextInput
                      style={[styles.charLimitInput, { backgroundColor: colors.secondary, borderColor: colors.border, color: colors.text }]}
                      placeholder="Custom"
                      placeholderTextColor={colors.textTertiary}
                      value={characterMaximum !== null && ![500, 1500, 2500, 5000].includes(characterMaximum) ? characterMaximum.toString() : ''}
                      onChangeText={(text) => {
                        const num = parseInt(text);
                        if (!isNaN(num) && num > 0) {
                          setCharacterMaximum(num);
                        } else if (text === '') {
                          setCharacterMaximum(null);
                        }
                      }}
                      keyboardType="numeric"
                    />
                  </View>
                </View>
              </View>
            )}

            <View style={styles.buttonRow}>
              <TouchableOpacity 
                style={styles.secondaryButton} 
                onPress={() => setStep(1)}
              >
                <Text style={styles.secondaryButtonText}>← Back</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={styles.primaryButton} 
                onPress={handleNext}
              >
                <Text style={styles.primaryButtonText}>Next: Select Examples →</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {/* STEP 3: Example Selection */}
        {step === 3 && (
          <View style={[styles.stepContainer, styles.sectionSpacing]}>
            <Text style={[styles.stepTitle, { color: colors.text }]}>Step 3: Select Examples</Text>
            <Text style={[styles.stepDescription, { color: colors.textSecondary }]}>
              AI recommended {recommendedExamples.length} examples. You can select up to 6 total.
            </Text>

            <View style={styles.exampleActions}>
              <Text style={[styles.selectedCount, { color: colors.accent }]}>
                {selectedExamples.length} / 6 selected
              </Text>
              <TouchableOpacity 
                onPress={() => setShowExampleSelector(true)}
                style={[styles.browseButton, { backgroundColor: colors.secondary, borderColor: colors.border }]}
              >
                <Text style={[styles.browseButtonText, { color: colors.text }]}>Browse All Examples</Text>
              </TouchableOpacity>
            </View>

            {/* Selected Examples (Reorderable) */}
            {selectedExamples.map((example, index) => (
              <View key={example.id} style={[styles.selectedExampleCard, { ...glass, backgroundColor: colors.card, borderColor: colors.accent }]}>
                <View style={[styles.exampleNumberBadge, { backgroundColor: colors.accent }]}>
                  <Text style={[styles.exampleNumberText, { color: '#fff' }]}>#{index + 1}</Text>
                </View>
                <View style={{flex: 1}}>
                  <Text style={[styles.exampleTitle, { color: colors.text }]}>{example.title}</Text>
                  <Text style={[styles.exampleCategory, { color: colors.accent }]}>{example.category}</Text>
                  <Text style={[styles.exampleDescription, { color: colors.textSecondary }]} numberOfLines={2}>
                    {example.description}
                  </Text>
                </View>
                <View style={styles.exampleControls}>
                  <TouchableOpacity 
                    onPress={() => moveExample(index, 'up')}
                    disabled={index === 0}
                    style={[styles.moveButton, { backgroundColor: colors.secondary, borderColor: colors.border }, index === 0 && {opacity: 0.3}]}
                  >
                    <Text style={[styles.moveButtonText, { color: colors.text }]}>↑</Text>
                  </TouchableOpacity>
                  <TouchableOpacity 
                    onPress={() => moveExample(index, 'down')}
                    disabled={index === selectedExamples.length - 1}
                    style={[styles.moveButton, { backgroundColor: colors.secondary, borderColor: colors.border }, index === selectedExamples.length - 1 && {opacity: 0.3}]}
                  >
                    <Text style={[styles.moveButtonText, { color: colors.text }]}>↓</Text>
                  </TouchableOpacity>
                  <TouchableOpacity 
                    onPress={() => toggleExampleSelection(example)}
                    style={[styles.removeButton, { backgroundColor: '#ff4444' }]}
                  >
                    <Text style={[styles.removeButtonText, { color: '#fff' }]}>✕</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ))}

            <View style={styles.buttonRow}>
              <TouchableOpacity 
                style={styles.secondaryButton} 
                onPress={() => setStep(2)}
              >
                <Text style={styles.secondaryButtonText}>← Back</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={styles.primaryButton} 
                onPress={handleNext}
                disabled={loading}
              >
                {loading ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <Text style={styles.primaryButtonText}>Generate Proposal ✨</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        )}

        {/* STEP 4: Generated Proposal with Sidebars */}
        {step === 4 && (
          <View style={[styles.resultLayout, isMobile && styles.resultLayoutMobile]}>
            {/* Examples Sidebar - Left (Hidden on mobile, shown in modal) */}
            {!isMobile && showExamplesSidebar && (
              <View style={[styles.sidebarWithHeader, { backgroundColor: colors.secondary, borderRightColor: colors.border }]}>
                <View style={[styles.sidebarHeaderRow, { backgroundColor: colors.card, borderBottomColor: colors.border }]}>
                  <Text style={[styles.sidebarHeaderTitle, { color: colors.text }]}>Project Examples</Text>
                  <TouchableOpacity
                    style={styles.collapseSidebarBtn}
                    onPress={() => setShowExamplesSidebar(false)}
                  >
                    <Text style={[styles.collapseSidebarText, { color: colors.text }]}>←</Text>
                  </TouchableOpacity>
                </View>
                <ExamplesSidebar
                  allExamples={allExamples}
                  selectedExamples={selectedExamples}
                  recommendedExamples={recommendedExamples}
                  onToggleExample={(example) => {
                    const isSelected = selectedExamples.find(e => e.id === example.id);
                    if (isSelected) {
                      setSelectedExamples(selectedExamples.filter(e => e.id !== example.id));
                    } else {
                      if (selectedExamples.length >= 6) {
                        alert('Maximum 6 examples allowed');
                        return;
                      }
                      setSelectedExamples([...selectedExamples, example]);
                    }
                  }}
                  onMoveExample={(index, direction) => {
                    const newExamples = [...selectedExamples];
                    const targetIndex = direction === 'up' ? index - 1 : index + 1;
                    if (targetIndex < 0 || targetIndex >= newExamples.length) return;
                    [newExamples[index], newExamples[targetIndex]] = [newExamples[targetIndex], newExamples[index]];
                    setSelectedExamples(newExamples);
                  }}
                  updating={false}
                />
              </View>
            )}
            
            {/* Show Examples Sidebar Button (when collapsed) */}
            {!isMobile && !showExamplesSidebar && (
              <TouchableOpacity
                style={styles.showLeftSidebarButton}
                onPress={() => setShowExamplesSidebar(true)}
              >
                <Text style={styles.showSidebarBtnText}>📁 →</Text>
              </TouchableOpacity>
            )}

            {/* Main Content Area - Center */}
            <View style={[styles.resultMainContent, isMobile && styles.resultMainContentMobile, isTablet && styles.resultMainContentTablet]}>
              {/* Mobile Action Buttons */}
              {isMobile && (
                <View style={styles.mobileActionBar}>
                  <TouchableOpacity 
                    style={styles.mobileActionButton}
                    onPress={() => setShowMobileExamples(true)}
                  >
                    <Text style={styles.mobileActionButtonText}>📁 Examples ({selectedExamples.length})</Text>
                  </TouchableOpacity>
                  {aiRecommendations.length > 0 && (
                    <TouchableOpacity 
                      style={styles.mobileActionButton}
                      onPress={() => setShowMobileAssistant(true)}
                    >
                      <Text style={styles.mobileActionButtonText}>🤖 Assistant</Text>
                    </TouchableOpacity>
                  )}
                </View>
              )}
              {/* Proposal Display */}
              <ProposalDisplay
                proposal={generatedProposal}
                editableProposal={editableProposal}
                isEditing={isEditing}
                loading={loading}
                onEdit={setEditableProposal}
                onSaveEdit={() => {
                  if (isEditing) {
                    setGeneratedProposal(editableProposal);
                  }
                  setIsEditing(!isEditing);
                }}
                onRegenerate={handleRegenerateProposal}
                onCopy={copyToClipboard}
                onCopyForContra={handleCopyForContra}
                onToggleAssistant={() => setShowAssistant(!showAssistant)}
                showAssistant={showAssistant}
                contraLoading={contraLoading}
              />

              {/* Question Answers Section - MOVED TO TOP */}
              {Object.keys(questionAnswers).length > 0 && (
                <View style={styles.qaSection}>
                  <Text style={[styles.qaTitle, { color: colors.accent }]}>Proposal Q&A Answers</Text>
                  <Text style={[styles.qaSubtitle, { color: colors.textSecondary }]}>
                    Copy and paste these into the client's question fields
                  </Text>
                  
                  {customQuestions.filter(q => q.trim()).map((question, index) => {
                    const answerKey = `answer${index + 1}`;
                    const answer = questionAnswers[answerKey];
                    
                    // Convert answer to string if it's an object (safety check)
                    const answerText = typeof answer === 'string' ? answer : (answer ? JSON.stringify(answer) : '');
                    
                    if (!answerText) return null;
                    
                    return (
                      <View key={index} style={[styles.qaItem, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
                        <View style={styles.qaQuestionHeader}>
                          <Text style={[styles.qaQuestionNumber, { color: colors.accent }]}>Question {index + 1}</Text>
                          <TouchableOpacity
                            style={[styles.qaCopyButton, { backgroundColor: colors.secondary, borderColor: colors.border }]}
                            onPress={() => copyToClipboard(answerText, 'Answer copied!')}
                          >
                            <Text style={[styles.qaCopyButtonText, { color: colors.text }]}>Copy Answer</Text>
                          </TouchableOpacity>
                        </View>
                        <Text style={[styles.qaQuestion, { color: colors.text }]}>{question}</Text>
                        <View style={styles.qaAnswerContainer}>
                          <TextInput
                            style={[styles.qaAnswer, { backgroundColor: colors.secondary, borderColor: colors.border, color: colors.text }]}
                            value={answerText}
                            onChangeText={(text) => {
                              setQuestionAnswers({
                                ...questionAnswers,
                                [answerKey]: text
                              });
                            }}
                            multiline
                          />
                        </View>
                      </View>
                    );
                  })}
                </View>
              )}

              {/* Original Project Description Section - MOVED BELOW Q&A, COLLAPSIBLE */}
              <View style={[styles.projectDescriptionSection, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
                <TouchableOpacity 
                  style={styles.projectDescriptionHeader}
                  onPress={() => setShowProjectDescription(!showProjectDescription)}
                >
                  <Text style={[styles.projectDescriptionTitle, { color: colors.accent }]}>
                    {showProjectDescription ? '▼' : '▶'} Original Project Description
                  </Text>
                  <Text style={[styles.projectDescriptionToggle, { color: colors.textSecondary }]}>
                    {showProjectDescription ? 'Hide' : 'Show'}
                  </Text>
                </TouchableOpacity>
                {showProjectDescription && (
                  <View style={[styles.projectDescriptionBox, { backgroundColor: colors.secondary, borderColor: colors.border }]}>
                    <Text style={[styles.projectDescriptionText, { color: colors.textSecondary }]}>{projectDescription}</Text>
                  </View>
                )}
              </View>
            </View>

            {/* AI Assistant Sidebar - Right (Hidden on mobile, shown in modal) */}
            {!isMobile && showAssistant && showAISidebar && aiRecommendations.length > 0 && (
              <View style={[styles.sidebarWithHeader, { backgroundColor: colors.secondary, borderRightColor: colors.border }]}>
                <View style={[styles.sidebarHeaderRow, { backgroundColor: colors.card, borderBottomColor: colors.border }]}>
                  <Text style={[styles.sidebarHeaderTitle, { color: colors.text }]}>AI Assistant</Text>
                  <TouchableOpacity
                    style={styles.collapseSidebarBtn}
                    onPress={() => setShowAISidebar(false)}
                  >
                    <Text style={[styles.collapseSidebarText, { color: colors.text }]}>→</Text>
                  </TouchableOpacity>
                </View>
                <View style={[styles.aiSidebarContainer, { backgroundColor: colors.secondary, borderLeftColor: colors.border }]}>
                  <AIAssistantPanel
                    recommendations={aiRecommendations}
                    completedTasks={completedTasks}
                    ignoredTasks={ignoredTasks}
                    onToggleTask={toggleTask}
                  />
                </View>
              </View>
            )}
            
            {/* Show AI Sidebar Button (when collapsed) */}
            {!isMobile && showAssistant && !showAISidebar && aiRecommendations.length > 0 && (
              <TouchableOpacity
                style={styles.showRightSidebarButton}
                onPress={() => setShowAISidebar(true)}
              >
                <Text style={styles.showSidebarBtnText}>← 🤖</Text>
              </TouchableOpacity>
            )}
          </View>
        )}
      </ScrollView>

      {/* Example Selector Modal */}
      <Modal
        visible={showExampleSelector}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowExampleSelector(false)}
      >
        <View style={[styles.modalOverlay, { backgroundColor: 'rgba(0, 0, 0, 0.7)' }]}>
          <View style={[styles.modalContent, { backgroundColor: colors.primary, borderColor: colors.border }]}>
            <View style={[styles.modalHeader, { borderBottomColor: colors.border }]}>
              <Text style={[styles.modalTitle, { color: colors.text }]}>Select Examples ({selectedExamples.length}/6)</Text>
              <TouchableOpacity onPress={() => setShowExampleSelector(false)}>
                <Text style={[styles.modalClose, { color: colors.text }]}>✕</Text>
              </TouchableOpacity>
            </View>

            <ScrollView style={{flex: 1}}>
              {/* Organization Examples */}
              {allExamples.some(e => e.scope === 'organization') && (
                <>
                  <View style={[styles.exampleSectionHeader, { borderBottomColor: colors.border }]}>
                    <Text style={[styles.exampleSectionTitle, { color: colors.accent }]}>🏢 Organization Examples</Text>
                    <Text style={[styles.exampleSectionSubtitle, { color: colors.textSecondary }]}>
                      Shared with your team
                    </Text>
                  </View>
                  {allExamples.filter(e => e.scope === 'organization').map((example) => {
                    const isSelected = selectedExamples.find(e => e.id === example.id);
                    const isRecommended = recommendedExamples.find(e => e.id === example.id);
                    
                    return (
                      <TouchableOpacity
                        key={example.id}
                        style={[
                          styles.modalExampleCard,
                          { ...glass, backgroundColor: colors.card, borderColor: colors.border },
                          isSelected && { borderColor: colors.accent, borderWidth: 2 }
                        ]}
                        onPress={() => toggleExampleSelection(example)}
                      >
                        <View style={[
                          styles.checkbox,
                          { borderColor: colors.border },
                          isSelected && { borderColor: colors.accent, backgroundColor: colors.accent }
                        ]}>
                          {isSelected && <Text style={{ color: '#fff', fontSize: 14, fontWeight: 'bold' }}>✓</Text>}
                        </View>
                        <View style={{flex: 1}}>
                          <View style={styles.titleRow}>
                            <Text style={[styles.modalExampleTitle, { color: colors.text }]}>{example.title}</Text>
                            {isRecommended && (
                              <View style={[styles.recommendedBadge, { backgroundColor: colors.accent }]}>
                                <Text style={[styles.recommendedText, { color: '#fff' }]}>AI Pick</Text>
                              </View>
                            )}
                          </View>
                          <Text style={[styles.modalExampleCategory, { color: colors.accent }]}>{example.category}</Text>
                          <Text style={[styles.modalExampleDescription, { color: colors.textSecondary }]} numberOfLines={2}>
                            {example.description}
                          </Text>
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </>
              )}

              {/* Personal Examples */}
              {allExamples.some(e => e.scope === 'personal') && (
                <>
                  <View style={[styles.exampleSectionHeader, { borderBottomColor: colors.border, marginTop: allExamples.some(e => e.scope === 'organization') ? 30 : 0 }]}>
                    <Text style={[styles.exampleSectionTitle, { color: colors.accent }]}>👤 My Personal Examples</Text>
                    <Text style={[styles.exampleSectionSubtitle, { color: colors.textSecondary }]}>
                      Only visible to you
                    </Text>
                  </View>
                  {allExamples.filter(e => e.scope === 'personal').map((example) => {
                    const isSelected = selectedExamples.find(e => e.id === example.id);
                    const isRecommended = recommendedExamples.find(e => e.id === example.id);
                    
                    return (
                      <TouchableOpacity
                        key={example.id}
                        style={[
                          styles.modalExampleCard,
                          { ...glass, backgroundColor: colors.card, borderColor: colors.border },
                          isSelected && { borderColor: colors.accent, borderWidth: 2 }
                        ]}
                        onPress={() => toggleExampleSelection(example)}
                      >
                        <View style={[
                          styles.checkbox,
                          { borderColor: colors.border },
                          isSelected && { borderColor: colors.accent, backgroundColor: colors.accent }
                        ]}>
                          {isSelected && <Text style={{ color: '#fff', fontSize: 14, fontWeight: 'bold' }}>✓</Text>}
                        </View>
                        <View style={{flex: 1}}>
                          <View style={styles.titleRow}>
                            <Text style={[styles.modalExampleTitle, { color: colors.text }]}>{example.title}</Text>
                            {isRecommended && (
                              <View style={[styles.recommendedBadge, { backgroundColor: colors.accent }]}>
                                <Text style={[styles.recommendedText, { color: '#fff' }]}>AI Pick</Text>
                              </View>
                            )}
                          </View>
                          <Text style={[styles.modalExampleCategory, { color: colors.accent }]}>{example.category}</Text>
                          <Text style={[styles.modalExampleDescription, { color: colors.textSecondary }]} numberOfLines={2}>
                            {example.description}
                          </Text>
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </>
              )}
            </ScrollView>

            <View style={[styles.modalActions, { borderTopColor: colors.border, backgroundColor: colors.secondary }]}>
              {step === 4 && (
                <TouchableOpacity 
                  style={[styles.modalButton, { backgroundColor: colors.accent, flex: 2 }]}
                  onPress={() => {
                    setShowExampleSelector(false);
                    handleUpdateProposalWithExamples();
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <ActivityIndicator color="#fff" size="small" />
                  ) : (
                    <Text style={[styles.modalButtonText, { color: '#fff' }]}>Update Proposal</Text>
                  )}
                </TouchableOpacity>
              )}
              <TouchableOpacity 
                style={[styles.modalButton, { backgroundColor: colors.accent }]}
                onPress={() => setShowExampleSelector(false)}
              >
                <Text style={[styles.modalButtonText, { color: '#fff' }]}>Done</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Mobile Examples Modal */}
      <Modal
        visible={showMobileExamples}
        animationType="slide"
        transparent={false}
        onRequestClose={() => setShowMobileExamples(false)}
      >
        <View style={styles.mobileModalContainer}>
          <View style={styles.mobileModalHeader}>
            <Text style={styles.mobileModalTitle}>Project Examples</Text>
            <TouchableOpacity onPress={() => setShowMobileExamples(false)}>
              <Text style={styles.mobileModalClose}>✕</Text>
            </TouchableOpacity>
          </View>
          <ScrollView style={styles.mobileModalContent}>
            <ExamplesSidebar
              allExamples={allExamples}
              selectedExamples={selectedExamples}
              recommendedExamples={recommendedExamples}
              onToggleExample={(example) => {
                const isSelected = selectedExamples.find(e => e.id === example.id);
                if (isSelected) {
                  setSelectedExamples(selectedExamples.filter(e => e.id !== example.id));
                } else {
                  if (selectedExamples.length >= 6) {
                    alert('Maximum 6 examples allowed');
                    return;
                  }
                  setSelectedExamples([...selectedExamples, example]);
                }
              }}
              onMoveExample={(index, direction) => {
                const newExamples = [...selectedExamples];
                const targetIndex = direction === 'up' ? index - 1 : index + 1;
                if (targetIndex < 0 || targetIndex >= newExamples.length) return;
                [newExamples[index], newExamples[targetIndex]] = [newExamples[targetIndex], newExamples[index]];
                setSelectedExamples(newExamples);
              }}
              updating={false}
            />
          </ScrollView>
          <View style={styles.mobileModalFooter}>
            <TouchableOpacity 
              style={styles.mobileModalButton}
              onPress={() => setShowMobileExamples(false)}
            >
              <Text style={styles.mobileModalButtonText}>Done</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Mobile Assistant Modal */}
      <Modal
        visible={showMobileAssistant}
        animationType="slide"
        transparent={false}
        onRequestClose={() => setShowMobileAssistant(false)}
      >
        <View style={styles.mobileModalContainer}>
          <View style={styles.mobileModalHeader}>
            <Text style={styles.mobileModalTitle}>AI Assistant</Text>
            <TouchableOpacity onPress={() => setShowMobileAssistant(false)}>
              <Text style={styles.mobileModalClose}>✕</Text>
            </TouchableOpacity>
          </View>
          <ScrollView style={styles.mobileModalContent}>
            {aiRecommendations.length > 0 && (
              <AIAssistantPanel
                recommendations={aiRecommendations}
                completedTasks={completedTasks}
                ignoredTasks={ignoredTasks}
                onToggleTask={toggleTask}
              />
            )}
          </ScrollView>
          <View style={styles.mobileModalFooter}>
            <TouchableOpacity 
              style={styles.mobileModalButton}
              onPress={() => setShowMobileAssistant(false)}
            >
              <Text style={styles.mobileModalButtonText}>Done</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Action Buttons - Fixed at Bottom (Step 4 only) */}
      {step === 4 && (
        <View style={[styles.actionButtonsContainer, { backgroundColor: colors.card, borderTopColor: colors.border }]}>
          <View style={styles.actionButtonsRow}>
            <TouchableOpacity
              style={[styles.startOverButton, { borderColor: colors.border, backgroundColor: colors.secondary }]}
              onPress={handleStartOver}
            >
              <Text style={[styles.startOverButtonText, { color: colors.text }]}>Start Over</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.newProposalButton, { borderColor: colors.accent, backgroundColor: colors.secondary }]}
              onPress={handleReset}
            >
              <Text style={[styles.newProposalButtonText, { color: colors.accent }]}>Create New Proposal</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Progress Indicator at Bottom */}
      <View style={[styles.bottomProgressContainer, { backgroundColor: colors.secondary, borderTopColor: colors.border }]}>
        <View style={styles.progressBar}>
          {[1, 2, 3, 4].map((s) => (
            <View 
              key={s} 
              style={[
                styles.progressStep, 
                { backgroundColor: colors.border },
                s <= step && [styles.progressStepActive, { backgroundColor: colors.accent }]
              ]} 
            />
          ))}
        </View>
        <Text style={[styles.progressText, { color: colors.textSecondary }]}>
          Step {step} of 4
        </Text>
      </View>

      {/* Tab Indicator */}
      <View style={[styles.tabContainer, { backgroundColor: colors.secondary, borderTopColor: colors.border }]}>
        <TouchableOpacity 
          style={[
            styles.tab, 
            step === 1 && { borderTopColor: colors.accent }
          ]}
          onPress={() => setStep(1)}
          disabled={step === 1}
        >
          <Text style={[
            styles.tabText, 
            { color: step === 1 ? colors.accent : colors.textSecondary }
          ]}>
            1. Description
          </Text>
        </TouchableOpacity>
        <TouchableOpacity 
          style={[
            styles.tab, 
            step === 2 && { borderTopColor: colors.accent }
          ]}
          onPress={() => step > 2 && setStep(2)}
          disabled={step < 2}
        >
          <Text style={[
            styles.tabText,
            { color: step === 2 ? colors.accent : step < 2 ? colors.textTertiary : colors.textSecondary }
          ]}>
            2. Template
          </Text>
        </TouchableOpacity>
        <TouchableOpacity 
          style={[
            styles.tab, 
            step === 3 && { borderTopColor: colors.accent }
          ]}
          onPress={() => step > 3 && setStep(3)}
          disabled={step < 3}
        >
          <Text style={[
            styles.tabText,
            { color: step === 3 ? colors.accent : step < 3 ? colors.textTertiary : colors.textSecondary }
          ]}>
            3. Examples
          </Text>
        </TouchableOpacity>
        <TouchableOpacity 
          style={[
            styles.tab, 
            step === 4 && { borderTopColor: colors.accent }
          ]}
          disabled={step < 4}
        >
          <Text style={[
            styles.tabText,
            { color: step === 4 ? colors.accent : step < 4 ? colors.textTertiary : colors.textSecondary }
          ]}>
            4. Result
          </Text>
        </TouchableOpacity>
      </View>
      
      <Toast
        visible={toast.visible}
        message={toast.message}
        type={toast.type}
        onHide={() => setToast({ ...toast, visible: false })}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: '#4a9eff',
    marginTop: 15,
    fontSize: 16,
  },
  statusBar: {
    width: 300,
    height: 6,
    backgroundColor: '#333',
    borderRadius: 3,
    marginTop: 20,
    overflow: 'hidden',
  },
  statusBarFill: {
    height: '100%',
    backgroundColor: '#4a9eff',
    borderRadius: 3,
  },
  estimateText: {
    color: '#999',
    marginTop: 15,
    fontSize: 14,
  },
  resetButton: {
    padding: 8, 
    marginRight: 10,
  },
  resetButtonText: {
    color: '#4a9eff',
    fontSize: 14,
  },
  navButton: {
    padding: 8,
    marginRight: 5,
  },
  navButtonText: {
    fontSize: 20,
    color: '#fff',
  },
  tabContainer: {
    flexDirection: 'row',
    backgroundColor: "#ffffff",
    borderTopWidth: 1,
    borderTopColor: '#333',
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 10,
    alignItems: 'center',
    borderTopWidth: 2,
    borderTopColor: 'transparent',
  },
  activeTab: {
    borderTopColor: '#4a9eff',
  },
  tabText: {
    color: '#666',
    fontSize: 13,
    fontWeight: '500',
  },
  activeTabText: {
    color: '#4a9eff',
    fontWeight: '600',
  },
  disabledTabText: {
    color: '#444',
  },
  bottomProgressContainer: {
    backgroundColor: "#f8fafc",
    paddingVertical: 12,
    paddingHorizontal: 15,
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: '#333',
  },
  progressBar: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 8,
  },
  progressStep: {
    width: 60,
    height: 4,
    backgroundColor: '#333',
    borderRadius: 2,
  },
  progressStepActive: {
    backgroundColor: '#4a9eff',
  },
  progressText: {
    color: '#aaa',
    fontSize: 12,
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 20,
    paddingTop: 120,
    width: '100%',
    paddingBottom: 100,
    flexGrow: 1,
  },
  contentContainerFull: {
    paddingTop: 120,
    paddingBottom: 100,
    flexGrow: 1,
  },
  resultLayout: {
    flex: 1,
    flexDirection: 'row',
    minHeight: '100%',
  },
  resultLayoutMobile: {
    flexDirection: 'column',
  },
  resultMainContent: {
    flex: 1,
    padding: 20,
  },
  resultMainContentMobile: {
    padding: 15,
    width: '100%',
  },
  resultMainContentTablet: {
    padding: 20,
    maxWidth: '100%',
  },
  mobileActionBar: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 15,
  },
  mobileActionButton: {
    flex: 1,
    backgroundColor: "#f8fafc",
    borderWidth: 1,
    borderColor: '#4a9eff',
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
  },
  mobileActionButtonText: {
    color: '#4a9eff',
    fontSize: 14,
    fontWeight: '600',
  },
  mobileModalContainer: {
    flex: 1,
    backgroundColor: "#ffffff",
  },
  mobileModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 15,
    backgroundColor: "#f8fafc",
    borderBottomWidth: 1,
    borderBottomColor: '#333',
  },
  mobileModalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#fff',
  },
  mobileModalClose: {
    fontSize: 24,
    color: '#aaa',
    fontWeight: 'bold',
  },
  mobileModalContent: {
    flex: 1,
  },
  mobileModalFooter: {
    padding: 15,
    backgroundColor: "#f8fafc",
    borderTopWidth: 1,
    borderTopColor: '#333',
  },
  mobileModalButton: {
    backgroundColor: '#4a9eff',
    borderRadius: 8,
    padding: 15,
    alignItems: 'center',
  },
  mobileModalButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  actionButtonsContainer: {
    backgroundColor: "#ffffff",
    paddingHorizontal: 20,
    paddingVertical: 15,
    borderTopWidth: 1,
    borderTopColor: '#333',
  },
  actionButtonsRow: {
    flexDirection: 'row',
    gap: 12,
    justifyContent: 'center',
  },
  startOverButton: {
    flex: 1,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    maxWidth: 200,
  },
  startOverButtonText: {
    color: '#64748b',
    fontSize: 14,
    fontWeight: '600',
  },
  newProposalButton: {
    flex: 1,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#4a9eff',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    maxWidth: 200,
  },
  newProposalButtonText: {
    color: '#4a9eff',
    fontSize: 14,
    fontWeight: '600',
  },
  aiSidebarContainer: {
    width: 320,
    borderLeftWidth: 1,
    padding: 15,
    maxHeight: '100%',
    overflow: 'scroll',
  },
  // Collapsible Sidebar Controls
  sidebarWithHeader: {
    borderRightWidth: 1,
  },
  sidebarHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 15,
    borderBottomWidth: 1,
  },
  sidebarHeaderTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#fff',
  },
  collapseSidebarBtn: {
    padding: 5,
  },
  collapseSidebarText: {
    fontSize: 20,
    color: '#4a9eff',
    fontWeight: 'bold',
  },
  showLeftSidebarButton: {
    position: 'absolute',
    left: 0,
    top: '50%',
    backgroundColor: '#4a9eff',
    padding: 12,
    borderTopRightRadius: 8,
    borderBottomRightRadius: 8,
    zIndex: 100,
  },
  showRightSidebarButton: {
    position: 'absolute',
    right: 0,
    top: '50%',
    backgroundColor: '#4a9eff',
    padding: 12,
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
    zIndex: 100,
  },
  showSidebarBtnText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  stepContainer: {
    width: '100%',
  },
  stepTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#fff',
    marginTop: 40,
    marginBottom: 10,
  },
  stepDescription: {
    fontSize: 14,
    color: '#aaa',
    marginBottom: 20,
    lineHeight: 20,
  },
  textArea: {
    borderRadius: 12,
    padding: 15,
    color: '#fff',
    fontSize: 14,
    minHeight: 250,
    textAlignVertical: 'top',
    borderWidth: 1,
    marginBottom: 20,
  },
  input: {
    borderRadius: 12,
    padding: 14,
    fontSize: 16,
    borderWidth: 1,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '600',
    marginTop: 20,
  },
  primaryButton: {
    backgroundColor: '#4a9eff',
    borderRadius: 8,
    padding: 15,
    alignItems: 'center',
    marginBottom: 10,
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  secondaryButton: {
    backgroundColor: 'transparent',
    borderRadius: 8,
    padding: 15,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: "#e2e8f0",
    marginBottom: 10,
    flex: 1,
  },
  secondaryButtonText: {
    color: '#aaa',
    fontSize: 16,
    fontWeight: '600',
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 10,
  },
  selectionCard: {
    borderRadius: 12,
    padding: 20,
    marginBottom: 15,
    borderWidth: 1,
  },
  selectedCard: {
    borderColor: '#4a9eff',
    borderWidth: 2,
  },
  radioContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  radio: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#666',
    marginTop: 2,
  },
  radioSelected: {
    borderColor: '#4a9eff',
    backgroundColor: '#4a9eff',
  },
  selectionTitle: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 5,
  },
  selectionPreview: {
    color: '#aaa',
    fontSize: 14,
    lineHeight: 20,
  },
  templatesSectionHeader: {
    marginBottom: 15,
    paddingBottom: 10,
    borderBottomWidth: 2,
  },
  templatesSectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 4,
  },
  templatesSectionSubtitle: {
    fontSize: 13,
    opacity: 0.8,
  },
  popularBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    flexDirection: 'row',
    alignItems: 'center',
  },
  popularBadgeText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '600',
  },
  exampleActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
  },
  selectedCount: {
    color: '#4a9eff',
    fontSize: 14,
    fontWeight: '600',
  },
  browseButton: {
    backgroundColor: '#333',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  browseButtonText: {
    color: '#fff',
    fontSize: 12,
  },
  selectedExampleCard: {
    flexDirection: 'row',
    backgroundColor: "#f8fafc",
    borderRadius: 8,
    padding: 12,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: '#4a9eff',
    gap: 10,
  },
  exampleNumberBadge: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#4a9eff',
    justifyContent: 'center',
    alignItems: 'center',
  },
  exampleNumberText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 14,
  },
  exampleTitle: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 2,
  },
  exampleCategory: {
    color: '#4a9eff',
    fontSize: 11,
    marginBottom: 4,
  },
  exampleDescription: {
    color: '#aaa',
    fontSize: 12,
    lineHeight: 16,
  },
  exampleControls: {
    flexDirection: 'row',
    gap: 5,
  },
  moveButton: {
    width: 30,
    height: 30,
    backgroundColor: '#333',
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  moveButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  removeButton: {
    width: 30,
    height: 30,
    backgroundColor: '#ff4444',
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  removeButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  proposalCard: {
    backgroundColor: "#f8fafc",
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#4a9eff',
  },
  proposalText: {
    color: '#fff',
    lineHeight: 24,
    fontSize: 15,
  },
  editingLabel: {
    color: '#4a9eff',
    fontWeight: '600',
    marginBottom: 8,
  },
  actionButtons: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 10,
  },
  actionButton: {
    flex: 1,
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  actionButtonText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 14,
  },
  resultExampleSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  resultExampleTitle: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  modifyExamplesButton: {
    backgroundColor: '#333',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  modifyExamplesText: {
    color: '#fff',
    fontSize: 12,
  },
  exampleChips: {
    flexDirection: 'row',
    marginBottom: 20,
  },
  chip: {
    backgroundColor: '#1a2a3a',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    marginRight: 8,
  },
  chipText: {
    color: '#4a9eff',
    fontSize: 12,
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.95)',
    justifyContent: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: "#f8fafc",
    borderRadius: 12,
    padding: 20,
    maxHeight: '80%',
    borderWidth: 1,
    borderColor: "#e2e8f0",
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#fff',
  },
  modalClose: {
    color: '#aaa',
    fontSize: 24,
    fontWeight: 'bold',
  },
  modalExampleCard: {
    flexDirection: 'row',
    backgroundColor: "#ffffff",
    borderRadius: 8,
    padding: 12,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: "#e2e8f0",
    gap: 12,
  },
  modalExampleCardSelected: {
    borderColor: '#4a9eff',
    backgroundColor: '#1a2a3a',
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: '#666',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 2,
  },
  checkboxChecked: {
    width: 14,
    height: 14,
    borderRadius: 2,
    backgroundColor: '#4a9eff',
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  modalExampleTitle: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
  },
  modalExampleCategory: {
    color: '#4a9eff',
    fontSize: 11,
    marginTop: 2,
    marginBottom: 4,
  },
  modalExampleDescription: {
    color: '#aaa',
    fontSize: 12,
    lineHeight: 16,
  },
  recommendedBadge: {
    backgroundColor: '#6a5aff',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  recommendedText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '600',
  },
  modalActions: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 15,
  },
  modalButton: {
    flex: 1,
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  modalButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  strictModeContainer: {
    backgroundColor: "#f8fafc",
    borderRadius: 8,
    padding: 15,
    marginTop: 20,
    borderWidth: 2,
    borderColor: '#ffa94d',
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: '#666',
    marginTop: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxChecked: {
    borderColor: '#ffa94d',
    backgroundColor: '#ffa94d',
  },
  checkmark: {
    color: '#fff',
    fontSize: 14,
    fontWeight: 'bold',
  },
  checkboxLabel: {
    color: '#ffa94d',
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  checkboxDesc: {
    color: '#aaa',
    fontSize: 13,
    lineHeight: 18,
  },
  commonQuestionsContainer: {
    backgroundColor: "#f8fafc",
    borderRadius: 8,
    padding: 15,
    marginTop: 0,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#4a9eff',
  },
  commonQuestionsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6a5aff',
    marginBottom: 12,
  },
  commonQuestionsGrid: {
    gap: 8,
  },
  commonQuestionChip: {
    backgroundColor: "#f1f5f9",
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#444',
  },
  commonQuestionChipText: {
    color: '#aaa',
    fontSize: 13,
    lineHeight: 18,
  },
  customQuestionButton: {
    backgroundColor: "#f1f5f9",
    borderRadius: 8,
    padding: 12,
    marginTop: 10,
    borderWidth: 1,
    borderColor: '#4a9eff',
    alignItems: 'center',
  },
  customQuestionButtonText: {
    color: '#4a9eff',
    fontSize: 14,
    fontWeight: '600',
  },
  addQuestionLabel: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8,
  },
  addQuestionSubtext: {
    fontSize: 13,
    marginBottom: 15,
    lineHeight: 18,
  },
  customQuestionInputRow: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 20,
    alignItems: 'flex-start',
  },
  customQuestionInput: {
    flex: 1,
    borderRadius: 8,
    padding: 15,
    fontSize: 14,
    minHeight: 80,
    textAlignVertical: 'top',
    borderWidth: 1,
  },
  addQuestionButton: {
    paddingVertical: 15,
    paddingHorizontal: 20,
    borderRadius: 8,
    minHeight: 80,
    justifyContent: 'center',
    alignItems: 'center',
  },
  addQuestionButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  questionsContainer: {
    backgroundColor: "#f8fafc",
    borderRadius: 8,
    padding: 15,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: "#e2e8f0",
  },
  questionsTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#4a9eff',
    marginBottom: 5,
  },
  questionsDesc: {
    fontSize: 13,
    color: '#aaa',
    marginBottom: 15,
    lineHeight: 18,
  },
  questionRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 15,
    gap: 10,
  },
  questionNumber: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#4a9eff',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 8,
  },
  questionNumberText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: 'bold',
  },
  questionInput: {
    flex: 1,
    backgroundColor: "#ffffff",
    borderRadius: 8,
    padding: 12,
    color: '#fff',
    fontSize: 14,
    minHeight: 60,
    textAlignVertical: 'top',
    borderWidth: 1,
    borderColor: "#e2e8f0",
  },
  removeQuestionButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#ff4444',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 8,
  },
  removeQuestionText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  projectDescriptionSection: {
    marginTop: 30,
    marginBottom: 20,
    padding: 20,
    backgroundColor: "#f8fafc",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#e2e8f0",
    width: '100%',
    maxWidth: '100%',
  },
  projectDescriptionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
  },
  projectDescriptionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#4a9eff',
    marginBottom: 0,
  },
  projectDescriptionToggle: {
    fontSize: 14,
    fontWeight: '500',
    opacity: 0.7,
  },
  projectDescriptionBox: {
    backgroundColor: "#ffffff",
    borderRadius: 8,
    padding: 15,
    borderWidth: 1,
    borderColor: "#e2e8f0",
    marginTop: 12,
  },
  projectDescriptionText: {
    fontSize: 14,
    color: '#ccc',
    lineHeight: 22,
  },
  qaSection: {
    marginTop: 30,
    marginBottom: 20,
    maxHeight: 600,
  },
  qaTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#4a9eff',
    marginBottom: 5,
  },
  qaSubtitle: {
    fontSize: 14,
    color: '#aaa',
    marginBottom: 20,
  },
  qaItem: {
    backgroundColor: "#f8fafc",
    borderRadius: 8,
    padding: 15,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: "#e2e8f0",
    maxHeight: 400,
  },
  qaQuestionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  qaQuestionNumber: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#4a9eff',
    textTransform: 'uppercase',
  },
  qaCopyButton: {
    backgroundColor: '#4a9eff',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  qaCopyButtonText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  qaQuestion: {
    fontSize: 15,
    color: '#fff',
    marginBottom: 12,
    lineHeight: 22,
    fontWeight: '500',
  },
  qaAnswerContainer: {
    backgroundColor: "#ffffff",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e2e8f0",
    maxHeight: 250,
  },
  qaAnswer: {
    color: '#fff',
    fontSize: 14,
    padding: 12,
    minHeight: 120,
    maxHeight: 250,
    textAlignVertical: 'top',
    lineHeight: 20,
  },
  characterLimitRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#2a2a2a',
  },
  characterLimitLabel: {
    color: '#888',
    fontSize: 12,
    marginRight: 12,
  },
  characterLimitButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flex: 1,
  },
  charLimitBtn: {
    backgroundColor: "#f1f5f9",
    borderRadius: 4,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderWidth: 1,
    borderColor: "#e2e8f0",
  },
  charLimitBtnActive: {
    backgroundColor: '#4a9eff',
    borderColor: '#4a9eff',
  },
  charLimitBtnText: {
    color: '#666',
    fontSize: 11,
    fontWeight: '500',
  },
  charLimitBtnTextActive: {
    color: '#fff',
  },
  charLimitInput: {
    backgroundColor: "#f1f5f9",
    borderWidth: 1,
    borderColor: "#e2e8f0",
    borderRadius: 4,
    paddingVertical: 4,
    paddingHorizontal: 8,
    color: '#fff',
    fontSize: 11,
    minWidth: 60,
    textAlign: 'center',
  },
  exampleSectionHeader: {
    paddingBottom: 12,
    marginBottom: 20,
    marginTop: 10,
    borderBottomWidth: 1,
  },
  exampleSectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 4,
  },
  exampleSectionSubtitle: {
    fontSize: 13,
    fontStyle: 'italic',
  },
  // Mobile optimization styles
  proposalActionsMobile: {
    flexDirection: 'column',
    gap: 8,
  },
  mainContentScrollView: {
    flex: 1,
    width: '100%',
  },
  mainContentContainer: {
    paddingBottom: 100, // Space for fixed buttons
  },
  // Mobile responsive styles
  mobileActionBar: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 20,
    paddingHorizontal: 16,
  },
  mobileActionButton: {
    flex: 1,
    backgroundColor: '#4a9eff',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  mobileActionButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  // Questions section styles
  questionsSection: {
    marginTop: 30,
    marginBottom: 20,
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
    width: '100%',
    maxWidth: '100%',
  },
  questionsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    marginBottom: 15,
  },
  questionsTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 0,
  },
  questionsToggle: {
    fontSize: 14,
    fontWeight: '500',
  },
  questionsContent: {
    marginTop: 10,
  },
  questionsList: {
    marginTop: 15,
    marginBottom: 20,
  },
  addQuestionSection: {
    marginTop: 20,
  },
  // Extra spacing styles
  stepContainer: {
    padding: 30,
    marginBottom: 20,
  },
  sectionSpacing: {
    marginTop: 25,
    marginBottom: 25,
  },
});
